import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import constants from "../constants/constants";
import { useLocation } from "react-router-dom";
import { faEnvelope, faPhone } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState, useRef, useCallback } from "react";
import SEO, { pageSEO } from "../components/seo.component";
import { Link } from "react-router-dom";
import { useUserParams } from "../hooks/useUserParams";

const useAnimatedBorder = () => {
  const topLeftRef = useRef(null);
  const topRightRef = useRef(null);
  const bottomLeftRef = useRef(null);
  const bottomRightRef = useRef(null);
  const rightRef = useRef(null);
  const leftRef = useRef(null);

  useEffect(() => {
    const timeouts = [];

    // Top borders animation
    timeouts.push(
      setTimeout(() => {
        topLeftRef.current?.classList.remove("w-0");
        topLeftRef.current?.classList.add("w-half");
        topRightRef.current?.classList.remove("w-0");
        topRightRef.current?.classList.add("w-half");
      }, 10)
    );

    // Side borders animation
    timeouts.push(
      setTimeout(() => {
        leftRef.current?.classList.remove("h-0");
        leftRef.current?.classList.add("h-full");
        rightRef.current?.classList.remove("h-0");
        rightRef.current?.classList.add("h-full");
      }, 1000)
    );

    // Bottom borders animation
    timeouts.push(
      setTimeout(() => {
        bottomLeftRef.current?.classList.remove("w-0");
        bottomLeftRef.current?.classList.add("w-half");
        bottomRightRef.current?.classList.remove("w-0");
        bottomRightRef.current?.classList.add("w-half");
      }, 4000)
    );

    return () => timeouts.forEach((timeout) => clearTimeout(timeout));
  }, []);

  return {
    topLeftRef,
    topRightRef,
    bottomLeftRef,
    bottomRightRef,
    rightRef,
    leftRef,
  };
};

const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState(window.innerWidth);

  const onWindowResize = useCallback(() => {
    setWindowSize(window.innerWidth);
  }, []);

  useEffect(() => {
    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, [onWindowResize]);

  return windowSize;
};

const useScrollVisibility = () => {
  const slideRefs = useRef([]);

  const addToRefs = (el) => {
    if (el && !slideRefs.current.includes(el)) {
      slideRefs.current.push(el);
    }
  };

  const isScrolledIntoView = useCallback((elem) => {
    if (elem) {
      const rect = elem.getBoundingClientRect();
      const elemTop = rect.top;
      const elemBottom = rect.bottom;
      return elemTop >= 0 && elemBottom <= window.innerHeight;
    }
    return false;
  }, []);

  useEffect(() => {
    slideRefs.current.forEach((el) => {
      if (isScrolledIntoView(el)) {
        el?.classList.remove("slide-left");
      }
    });
  }, [isScrolledIntoView]);

  return { addToRefs };
};

export default function About() {
  const windowSize = useWindowSize();
  const {
    topLeftRef,
    topRightRef,
    bottomLeftRef,
    bottomRightRef,
    rightRef,
    leftRef,
  } = useAnimatedBorder();
  const { addToRefs } = useScrollVisibility();
  const {
    getCurrentAgentName,
    getCurrentAgentTitle,
    getCurrentBrokerage,
    getCurrentAbout,
    getCurrentHeadshot,
  } = useUserParams();

  const seoConfig = pageSEO.about();

  return (
    <>
      <SEO {...seoConfig} />
      <div className="bg-logo-main/90 p-8 md:p-12 mt-10 flex flex-col md:flex-row gap-y-10 gap-x-12 w-[90%] relative border border-gray-200 shadow-lg mx-auto">
      {/* Left Side: Text */}
      <div className="flex-1 flex flex-col justify-center relative z-10">
        {/* Faded ABOUT background */}
        <span
          className="absolute top-0 left-0 text-[7vw] md:text-[6vw] font-extrabold text-gray-200 opacity-10 select-none leading-none pointer-events-none -translate-y-2 md:-translate-y-4 font-serif"
          style={{ zIndex: 0 }}
        >
          ABOUT
        </span>
        <span
          className="text-[3vw] tracking-widest text-lux-gold font-semibold mb-2 relative z-10 font-serif"
          style={{ letterSpacing: "0.2em" }}
        >
          MEET
        </span>
        <h1 className="text-[6vw] md:text-[5vw] font-bold text-white mb-4 relative z-10 font-serif">
          {getCurrentAgentName()}
        </h1>
        <p className="text-gray-300 mb-4 relative z-10 font-semibold text-2xl">
          {getCurrentAbout()}
        </p>
        <p className="mb-8 relative z-10 text-2xl font-semibold text-lux-gold">
          {getCurrentAgentTitle()}
        </p>
        <p className="mb-8 relative z-10 text-2xl font-semibold text-lux-gold">
          {getCurrentBrokerage()}
        </p>
        <Link
          to="/about"
          className="bg-[#7c4a21] text-white px-8 py-3 rounded-sm font-semibold text-lg shadow-md w-fit relative z-10 hover:bg-[#5a3215] transition"
        >
          MEET {getCurrentAgentName()}
        </Link>
      </div>
      {/* Right Side: Image */}
      <div className="flex-1 flex items-center justify-center relative z-10">
        <div className="relative w-full max-w-md md:max-w-lg aspect-[3/4] flex items-center justify-center">
          {/* Background image */}
          <div
            className="absolute inset-0 rounded-md border-white shadow-xl bg-cover bg-center w-full h-full"
            style={{ backgroundImage: "url(" + constants.ABOUT_BG + ")", zIndex: 1 }}
          />
          {/* Profile image overlay */}
          <div className="relative flex items-center justify-center w-full h-full z-20">
            <img
              src={getCurrentHeadshot()}
              alt={getCurrentAgentName()}
              fill
              sizes="(max-width: 800px) 90vw, 600px"
              className="object-cover rounded-md scale-125"
              priority
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
